/**
 * Tenant shell sign-in (not tenant-admin).
 * Routed at `/login` — e.g. http://saas.localhost:8081/login (`apps/shell` Vite default port 8081).
 * Auth: `api.ts` → `authApi` in `services/apiClient.ts` (defaults to same-origin `/api` so the shell
 * Vite proxy forwards `/api/auth/*` to identity-config on :3010 unless `VITE_AUTH_API_URL` overrides).
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Eye, EyeOff, Lock, ShieldAlert } from 'lucide-react';
import {
  clearStoredLockUntil,
  extractLockedUntilIso,
  formatLockCountdownMmSs,
  readStoredLockUntilIso,
  remainingMsFromIso,
  writeStoredLockUntilIso,
} from '@/lib/loginLockout';
import { getShellAuthApiBaseUrl } from '@/services/apiClient';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, logout } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // Lock state
  const [lockDeadlineIso, setLockDeadlineIso] = useState<string | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // -----------------------------------------------------------
  // Restore lock from localStorage when email changes
  // -----------------------------------------------------------
  useEffect(() => {
    const email = formData.email.trim();
    if (!email) {
      setLockDeadlineIso(null);
      setRemainingMs(0);
      return;
    }
    const stored = readStoredLockUntilIso(email);
    setLockDeadlineIso(stored);
    setRemainingMs(stored ? Math.max(0, remainingMsFromIso(stored)) : 0);
  }, [formData.email]);

  // -----------------------------------------------------------
  // Live countdown ticker — updates every second
  // -----------------------------------------------------------
  useEffect(() => {
    // Clear any previous interval
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!lockDeadlineIso) {
      setRemainingMs(0);
      return;
    }

    // Set initial value immediately
    const initial = remainingMsFromIso(lockDeadlineIso);
    setRemainingMs(Math.max(0, initial));

    if (initial <= 0) {
      // Already expired — clean up storage
      const email = formData.email.trim();
      if (email) clearStoredLockUntil(email);
      setLockDeadlineIso(null);
      return;
    }

    timerRef.current = setInterval(() => {
      const rem = remainingMsFromIso(lockDeadlineIso);
      if (rem <= 0) {
        setRemainingMs(0);
        setLockDeadlineIso(null);
        const email = formData.email.trim();
        if (email) clearStoredLockUntil(email);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = null;
      } else {
        setRemainingMs(rem);
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [lockDeadlineIso]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLocked = remainingMs > 0;
  const countdownDisplay = useMemo(
    () => (isLocked ? formatLockCountdownMmSs(remainingMs) : '00:00'),
    [isLocked, remainingMs],
  );

  // -----------------------------------------------------------
  // Redirect on auth change
  // -----------------------------------------------------------
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('newUser') === 'true') {
      logout();
      navigate('/login', { replace: true });
    }
  }, [location.search, logout, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // -----------------------------------------------------------
  // Submit handler
  // -----------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Guard: do not submit while locally locked (double-check)
    if (isLocked) return;

    setIsLoading(true);

    try {
      const step = await login(formData.email.trim(), formData.password);

      if (step?.requiresOtp && step.challengeId) {
        clearStoredLockUntil(formData.email.trim());
        setLockDeadlineIso(null);
        navigate('/login/verify-otp', {
          replace: true,
          state: {
            challengeId: step.challengeId,
            email: formData.email.trim(),
            from: (location.state as { from?: { pathname?: string } })?.from,
          },
        });
        toast({
          title: 'Check your email',
          description: 'We sent a verification code to your registered email address.',
        });
        return;
      }

      toast({
        title: 'Welcome back!',
        description: 'You have successfully logged in.',
      });
    } catch (error: unknown) {
      const err = error as Error & {
        statusCode?: number;
        lockedUntil?: string;
        authErrorPayload?: unknown;
      };

      const httpStatus = err.statusCode;
      // Subscription-expired short-circuit for the OTP-disabled login path
      // (the OTP path handles this in LoginVerifyOtp.tsx).
      const payload = err.authErrorPayload as { code?: string; expiredOn?: string | null } | undefined;
      if (httpStatus === 403 && payload?.code === 'SUBSCRIPTION_EXPIRED') {
        navigate('/subscription-expired', {
          replace: true,
          state: { expiredOn: payload.expiredOn ?? null, email: formData.email.trim() },
        });
        return;
      }
      const locked = httpStatus === 429;

      // Extract lockedUntil from error payload (declared here so it is in
      // scope for the toast message below, not just inside the `if` block)
      const lockIso = locked
        ? err.lockedUntil ??
          extractLockedUntilIso(err.authErrorPayload) ??
          readStoredLockUntilIso(formData.email.trim()) ??
          undefined
        : undefined;

      if (locked) {
        if (lockIso) {
          writeStoredLockUntilIso(formData.email.trim(), lockIso);
          setLockDeadlineIso(lockIso);
          setRemainingMs(Math.max(0, remainingMsFromIso(lockIso)));
        }

        // No toast needed — the inline banner is clear enough
      } else {
        // 401 or other non-lock error
        toast({
          title: 'Login failed',
          description: err.message || 'Invalid email or password.',
          variant: 'destructive',
        });
      }
      const remainingForMsg = lockIso ? remainingMsFromIso(lockIso) : 0;
      toast({
        title: locked ? 'Too many failed attempts' : 'Login failed',
        description: locked
          ? remainingForMsg > 0
            ? `Too many failed attempts. Try again in ${formatLockCountdownMmSs(remainingForMsg)}`
            : 'Too many failed attempts. Please try again in a moment.'
          : err.message || 'Invalid credentials',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // -----------------------------------------------------------
  // Render
  // -----------------------------------------------------------
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="DataCaffe Logo"
              className="h-16 w-16 object-contain"
            />
            <span className="text-2xl font-bold">
              <span style={{ color: '#f97316' }}>ESG</span>
              <span className="text-blue-600 dark:text-blue-400">Caffe</span>
            </span>
          </div>
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">Welcome back</CardTitle>
            <CardDescription className="text-center">
              Enter your credentials to access your ESG dashboard
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* ── Lockout Banner ── */}
              {isLocked && (
                <div
                  role="alert"
                  aria-live="polite"
                  className="flex flex-col gap-1 rounded-lg border border-red-300 bg-red-50 px-4 py-3 dark:border-red-800/60 dark:bg-red-950/40"
                >
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-semibold text-sm">
                    <ShieldAlert className="h-4 w-4 flex-shrink-0" />
                    Account temporarily locked
                  </div>
                  <p className="text-xs text-red-600 dark:text-red-400">
                    Too many failed login attempts. Please wait before trying again.
                  </p>
                  {/* Countdown */}
                  <div className="mt-1 flex items-center justify-center gap-2 rounded-md bg-red-100 dark:bg-red-900/40 py-2">
                    <Lock className="h-4 w-4 text-red-500 dark:text-red-400" />
                    <span className="font-mono text-xl font-bold text-red-700 dark:text-red-300 tabular-nums tracking-widest">
                      {countdownDisplay}
                    </span>
                    <span className="text-xs text-red-500 dark:text-red-400">remaining</span>
                  </div>
                </div>
              )}

              {/* ── Email ── */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="admin@datacaffe.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value.trim() })}
                  required
                  className="text-left"
                  disabled={isLoading}
                />
              </div>

              {/* ── Password ── */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    disabled={isLoading || isLocked}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* ── Submit Button ── */}
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || isLocked}
                id="sign-in-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : isLocked ? (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    Locked — {countdownDisplay}
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>

              {/* Dev hint */}
              {import.meta.env.DEV && (
                <p
                  className="text-center text-xs text-muted-foreground pt-2"
                  title="Override with VITE_AUTH_API_URL or VITE_API_URL in .env"
                >
                  <span className="font-medium text-foreground/70">Auth API base</span>
                  <br />
                  <code className="mt-1 inline-block max-w-full break-all rounded bg-muted/70 px-1.5 py-0.5 text-[11px]">
                    {getShellAuthApiBaseUrl()}
                  </code>
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
